import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import Pagination from './PaginationNew';
import { TripService } from '../services/tripService';
import type { Trip } from '../types/trip';

const TripsTable: React.FC = () => {
  const navigate = useNavigate();

  const { currentPage, itemsPerPage, setPage, setLimit } = usePagination({
    defaultPage: 1,
    defaultLimit: 50,
  });

  const [rows, setRows] = useState<Trip[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [type, setType] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [debouncedName, setDebouncedName] = useState<string>('');

  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  useEffect(() => {
    document.title = currentPage > 1 ? `Trips - Page ${currentPage}` : 'Trips';
  }, [currentPage]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedName(name), 400);
    return () => window.clearTimeout(timer);
  }, [name]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await TripService.listTrips({
          page: currentPage,
          limit: itemsPerPage,
          type: type.trim(),
          name: debouncedName.trim(),
          sort: '-createdAt',
        });

        if (!isMounted) return;
        setRows(res.items || []);
        setTotalPages(res.pages || 1);
        setTotalItems(typeof res.total === 'number' ? res.total : (res.items || []).length);
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load trips');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [currentPage, itemsPerPage, type, debouncedName]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const safeRows = useMemo(() => rows, [rows]);

  if (loading && safeRows.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading trips...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trips</h1>
          <p className="mt-1 text-sm text-gray-500">List trips (POST /api/trips)</p>
        </div>

        <div className="flex items-center space-x-3">
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              if (currentPage !== 1) setPage(1);
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            aria-label="Trip type"
          >
            <option value="">All types</option>
            <option value="Domestic">Domestic</option>
            <option value="International">International</option>
          </select>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (currentPage !== 1) setPage(1);
              }}
              className="block w-80 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeRows.map((t) => (
                <tr key={t._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{t.name || t.title || t.tripName || '—'}</div>
                    <div className="text-sm text-gray-500 break-all">{t._id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {t.type || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(t.createdAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(t.updatedAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      onClick={() => navigate(`/trips/${encodeURIComponent(t._id)}`)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}

              {!loading && safeRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                    No trips found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />
    </div>
  );
};

export default TripsTable;
